import { CheckboxInput, OtpInput } from '@rentnerkev/inputs'
import { Laptop, Loader2, ShieldCheck, ShieldX } from 'lucide-react'
import { motion } from 'motion/react'
import type { Variants } from 'motion/react'

import useTranslationStore from '@/language/useTranslationStore'

import type { TwoFactorFormProps } from '../Types/types'

const containerVariants: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            type: 'spring',
            damping: 25,
            stiffness: 250,
            staggerChildren: 0.08,
            when: 'beforeChildren',
        },
    },
}

const itemVariants: Variants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { type: 'spring', damping: 25, stiffness: 300 },
    },
}

export default function TwoFactorForm({
    code,
    isLoading,
    isError,
    trustThisDevice,
    onSubmit,
    onChange,
    inputRef,
    onTrustThisDeviceChange,
    onBackToLogin,
}: TwoFactorFormProps) {
    const { t } = useTranslationStore()

    return (
        <motion.form
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            onSubmit={onSubmit}
            className="flex flex-col gap-6"
        >
            <motion.div
                variants={itemVariants}
                className="flex flex-col gap-2 text-center"
            >
                <motion.div
                    animate={
                        isError
                            ? { scale: [1, 1.2, 1], x: [-5, 5, -5, 5, 0] }
                            : {}
                    }
                    transition={{ duration: 0.4 }}
                    className="mb-2 flex justify-center"
                >
                    {isError ? (
                        <ShieldX className="h-10 w-10 text-red-500 opacity-80" />
                    ) : (
                        <ShieldCheck className="text-primary h-10 w-10 opacity-80" />
                    )}
                </motion.div>
                <h2 className="text-xl font-semibold text-white">
                    {t('auth.twoFactor.title')}
                </h2>
                <p className="text-secondary-text text-sm">
                    {t('auth.twoFactor.description')}
                </p>
            </motion.div>

            <motion.div variants={itemVariants} className="my-4">
                <OtpInput
                    name="twoFactorCode"
                    value={code}
                    onValueChange={onChange}
                    triggerRef={inputRef}
                    length={6}
                    status={isError ? 'error' : 'idle'}
                    disabled={isLoading}
                    aria-label={t('auth.twoFactor.title')}
                    autoFocus
                    required
                />
            </motion.div>

            <motion.label
                variants={itemVariants}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-colors ${
                    trustThisDevice
                        ? 'border-primary/40 bg-primary/5'
                        : 'border-border-dark bg-background-dark/30 hover:border-border-dark/80'
                } ${isLoading ? 'pointer-events-none opacity-50' : ''}`}
            >
                <CheckboxInput
                    checked={trustThisDevice}
                    onChange={(event) =>
                        onTrustThisDeviceChange(event.target.checked)
                    }
                    disabled={isLoading}
                    className="accent-primary mt-0.5 size-5 shrink-0"
                />
                <div className="flex gap-2 text-left">
                    <Laptop className="text-primary/80 mt-0.5 h-4 w-4 shrink-0" />
                    <div>
                        <span className="block text-sm font-medium text-white">
                            {t('auth.twoFactor.rememberDevice')}
                        </span>
                        <span className="text-secondary-text mt-0.5 block text-xs leading-relaxed">
                            {t('auth.twoFactor.rememberDeviceDescription')}
                        </span>
                    </div>
                </div>
            </motion.label>

            <motion.button
                variants={itemVariants}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={isLoading}
                className="bg-primary hover:bg-primary-hover mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg px-8 py-3 font-bold text-white shadow-lg transition-colors disabled:cursor-not-allowed disabled:opacity-50"
            >
                {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                    t('userSettings.security.2faConfirm')
                )}
            </motion.button>

            <motion.div variants={itemVariants} className="mt-2 text-center">
                <button
                    type="button"
                    disabled={isLoading}
                    onClick={onBackToLogin}
                    className="text-secondary-text hover:text-primary cursor-pointer text-xs transition-colors hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {t('common.backToLogin')}
                </button>
            </motion.div>
        </motion.form>
    )
}
